import { useSyncExternalStore } from 'react';

let enabled = true;
let ready = false;
let readFailed = false;
const listeners = new Set<() => void>();
const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};
export const useTooltipsEnabled = () => useSyncExternalStore(subscribe, () => enabled);
export const useTooltipPreferenceReady = () => useSyncExternalStore(subscribe, () => ready);
export const useTooltipPreferenceError = () => useSyncExternalStore(subscribe, () => readFailed);
const publish = (value: boolean) => {
  enabled = value;
  ready = true;
  readFailed = false;
  listeners.forEach((listener) => listener());
};

/** Native title hints need the same preference as portal tooltips.
 * Keep an empty title while hidden so later React removal is observable.
 * Retain title-only accessible names; never remove existing ARIA labels.
 */
// standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
export function controlNativeTooltips(root: HTMLElement) {
  const saved = new Map<Element, { title: string; aria?: string }>();
  let observer: MutationObserver | undefined;
  const restoreAria = (element: Element, record: { title: string; aria?: string }) => {
    if (record.aria && element.getAttribute(record.aria) === record.title)
      element.removeAttribute(record.aria);
  };
  const suppress = (element: Element) => {
    const title = element.getAttribute('title');
    const previous = saved.get(element);
    if (title === null) {
      if (previous) restoreAria(element, previous);
      saved.delete(element);
      return;
    }
    if (!title) return;
    if (previous) restoreAria(element, previous);
    const named =
      element.hasAttribute('aria-label') ||
      element.hasAttribute('aria-labelledby') ||
      !!element.textContent?.trim() ||
      (element instanceof HTMLInputElement && !!element.labels?.length);
    const aria =
      !named && element.matches('button,a,input,select,textarea,[role="button"]')
        ? 'aria-label'
        : !element.hasAttribute('aria-description')
          ? 'aria-description'
          : undefined;
    saved.set(element, { title, aria });
    if (aria) element.setAttribute(aria, title);
    element.setAttribute('title', '');
  };
  const visit = (node: Node) => {
    if (!(node instanceof Element)) return;
    if (node.hasAttribute('title')) suppress(node);
    node.querySelectorAll('[title]').forEach(suppress);
  };
  const restore = (element: Element, record: { title: string; aria?: string }) => {
    if (element.getAttribute('title') === '') element.setAttribute('title', record.title);
    restoreAria(element, record);
  };
  const apply = (show: boolean) => {
    observer?.disconnect();
    observer = undefined;
    root.dataset.tooltips = show ? 'on' : 'off';
    if (show) {
      saved.forEach((record, element) => restore(element, record));
      saved.clear();
      return;
    }
    visit(root);
    observer = new MutationObserver((records) => {
      for (const record of records) {
        if (record.type === 'attributes') suppress(record.target as Element);
        else record.addedNodes.forEach(visit);
      }
      for (const [element, record] of saved)
        if (!root.contains(element)) {
          restore(element, record);
          saved.delete(element);
        }
    });
    observer.observe(root, {
      attributes: true,
      attributeFilter: ['title'],
      childList: true,
      subtree: true,
    });
  };
  return {
    apply,
    dispose: () => {
      observer?.disconnect();
      saved.forEach((record, element) => restore(element, record));
      saved.clear();
      delete root.dataset.tooltips;
    },
  };
}

export function installTooltipPreference() {
  const native = controlNativeTooltips(document.documentElement);
  const api = window.capturePointDesktop;
  let revision = 0;
  let alive = true;
  const receive = (value: boolean) => {
    if (!alive) return;
    revision++;
    publish(value);
    native.apply(value);
  };
  const stop = api?.onTooltipsChanged?.(receive);
  const requestedAt = revision;
  if (api?.getTooltips) {
    // Do not flash hints before a saved off preference has been read.
    enabled = false;
    native.apply(false);
    void api
      .getTooltips()
      .then((value) => {
        if (revision === requestedAt) receive(value);
      })
      .catch(() => {
        if (alive && revision === requestedAt) {
          enabled = true;
          readFailed = true;
          native.apply(true);
          listeners.forEach((listener) => listener());
        }
      });
  } else native.apply(enabled);
  return () => {
    alive = false;
    stop?.();
    native.dispose();
  };
}

export async function saveTooltipPreference(value: boolean) {
  const api = window.capturePointDesktop;
  if (!api?.setTooltips)
    throw new Error('Tooltip preferences are unavailable. Restart the app and try again.');
  const saved = await api.setTooltips(value);
  publish(saved);
  return saved;
}
